import {
  Autocomplete,
  CircularProgress,
  styled
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useEffect, useState, useCallback } from 'react';
import { asyncAutocompleteStyles, requiredAsteriskStyles } from '../../styles/inputStyles';
import TextField from './TextField';
import { inputBaseStyles } from '../../styles/inputStyles';
import CloseIcon from './icons/CloseIcon';

// Define the option type
export interface Option {
  value: string;
  label: string;
}

interface AsyncAutocompleteProps {
  label?: string;
  placeholder?: string;
  value: Option | null;
  onChange: (value: Option | null) => void;
  loadOptions: (searchQuery: string, otherQuery?: string) => Promise<Option[]>;
  width?: string;
  debounceMs?: number;
  otherQuery?: string;
}

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ...inputBaseStyles,
  ...requiredAsteriskStyles,
  ...asyncAutocompleteStyles,
}));

export const AsyncAutocomplete: React.FC<AsyncAutocompleteProps> = ({
  label,
  placeholder = '',
  value,
  onChange,
  loadOptions,
  width = '100%',
  debounceMs = 300,
  otherQuery = '',
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputValue);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, debounceMs]);

  // Fetch options when search query changes
  useEffect(() => {
    let active = true;

    if (!open) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const results = await loadOptions(searchQuery, otherQuery);
        if (active) {
          setOptions(results);
        }
      } catch (error) {
        console.error('Error loading options:', error);
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open, searchQuery, loadOptions]);

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <StyledAutocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setLoading(false);
        setOpen(false);
      }}
      value={value}
      onChange={(_, newValue) => onChange(newValue as Option | null)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      getOptionLabel={(option) => (option as Option)?.label || ''}
      isOptionEqualToValue={(option, value) => (option as Option)?.value === (value as Option)?.value}
      popupIcon={<KeyboardArrowDownIcon sx={{ width: '16px', height: '16px' }} />}
      clearIcon={<CloseIcon sx={{ width: '16px', height: '16px' }} />}
      slotProps={{
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': { fontSize: '12px !important', padding: '8px 14px' },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          width={width}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};